import type { ControllerOptions, WebController } from "@wishlink/applens-web";
import type { ScreenCompletion } from "@wishlink/applens-core";
import type {
  Clock,
  HomeHandle,
  HomeState,
  LocalConfig,
  DeploymentConfig,
} from "./contracts";
import { safely } from "./contracts";
import { RELEASE_2_ENDPOINTS } from "./registry";
import { createDiyJourney } from "./diyJourney";
import { createIntentDependency } from "./intentDependency";
import type { IntentSnapshot } from "./intentDependency";
import type { BrowserSignals } from "./browserSignals";
import type { ScreenHandle } from "@wishlink/applens-web";
import { HOME_TERMINAL_SCHEMA } from "./dependencies";
import type { DependencyTimings } from "./dependencies";

export const HOME_ERRORS = [
  { code: "home.blocking", category: "application" as const },
  { code: "home.partial", category: "application" as const },
];
export function homeCompletion(state: HomeState): ScreenCompletion | undefined {
  if (state.redirecting) return { outcome: "cancelled" };
  if (state.blockingError)
    return { outcome: "full_error", errorCode: "home.blocking" };
  if (
    state.configReady &&
    state.creatorReady &&
    state.branchCommitted &&
    !state.socialPending
  )
    return state.partialError
      ? { outcome: "degraded", errorCode: "home.partial" }
      : { outcome: "ready" };
  return undefined;
}
export function isLoopback(origin: string) {
  return safely(() => {
    const url = new URL(origin);
    return (
      ["http:", "https:"].includes(url.protocol) &&
      ["localhost", "127.0.0.1", "[::1]"].includes(url.hostname) &&
      url.origin === origin
    );
  }, false);
}
interface Dependencies {
  signals?: BrowserSignals;
  mode?: ControllerOptions["mode"];
  deploymentEndpoint?: ControllerOptions["destination"];
  clock: Clock;
  origin: string;
  bootstrapDeadline?: number;
  allowDeploymentPropagation?: boolean;
  loadBinding?: ControllerOptions["loadBinding"];
  schedule: ControllerOptions["schedule"];
  defer(work: () => void): void;
  lifecycle?: ControllerOptions["lifecycle"];
}
/** Creator composition: readiness, Strict Mode reference counting and RTK snapshots. */
export function createController(deps: Dependencies) {
  const mode = deps.mode ?? (isLoopback(deps.origin) ? "local" : "deployment");
  const destination = deps.deploymentEndpoint && { ...deps.deploymentEndpoint };
  const deploymentPropagation = deps.allowDeploymentPropagation === true;
  let sdk: WebController | undefined;
  let coreRules: typeof import("@wishlink/applens-core") | undefined;
  let attachHeaders:
    | typeof import("@wishlink/applens-web/requests").attachRequestHeaders
    | undefined;
  let generation = -1;
  let identity: string | undefined;
  let normalizeIdentity:
    | typeof import("@wishlink/applens-core").normalizeIdentity
    | undefined;
  let config: Readonly<LocalConfig> | undefined;
  let revision = 0;
  let removeSignals: (() => void) | undefined;
  let firstHome = true;
  const loaders = new Set<object>();
  const diy = createDiyJourney({
    now: deps.clock.now,
    schedule: deps.schedule,
    start: (entry) => {
      if (!active()) return;
      const journey = sdk?.startJourney("diy_single_product", { entry });
      loaders.forEach(() => journey?.loader("start"));
      return journey;
    },
  });
  let suspended = false;
  let hiddenAt: number | undefined;
  let readDependencies: () => DependencyTimings & {
    intent?: IntentSnapshot;
  } = () => ({});
  type Navigation = {
    handle: HomeHandle;
    references: number;
    state: HomeState;
    pending?: boolean;
    closed: boolean;
    route: string;
    attempt?: ScreenHandle;
    intent: ReturnType<typeof createIntentDependency>;
    launch?: number;
    content?: boolean;
    launchContent?: number;
    dependencies: ReturnType<
      typeof import("@wishlink/applens-core").createDependencies
    >;
  };
  let navigation: Navigation | undefined;
  // The SDK cancels the previous screen synchronously inside startScreen. Keep its
  // reader installed until that call returns, then install the new record's closure.
  let terminalReader:
    | (() => Readonly<Record<string, string | number | boolean>> | undefined)
    | undefined;
  const active = () => {
    if (config && deps.clock.epochNow() >= config.expiresAt) disable();
    return !suspended && config ? config : undefined;
  };
  function disable() {
    revision++;
    config = undefined;
    removeSignals?.();
    removeSignals = undefined;
    diy.disable();
    sdk?.disable();
  }
  async function configure(
    input: LocalConfig | DeploymentConfig,
    requestedMode: "local" | "deployment"
  ) {
    const nextGeneration = safely(() => input.generation, NaN);
    if (!Number.isSafeInteger(nextGeneration) || nextGeneration <= generation) {
      disable();
      return false;
    }
    generation = nextGeneration;
    disable();
    suspended = false;
    const expected = revision;
    // OFF/excluded activation does not fetch even the portable SDK control plane.
    if (!sdk && (input.enabled !== true || input.eligible !== true))
      return false;
    if (!sdk) {
      let loaded: [
        typeof import("@wishlink/applens-web"),
        typeof import("@wishlink/applens-web/requests"),
        typeof import("@wishlink/applens-core")
      ];
      try {
        loaded = await Promise.all([
          import(
            /* webpackChunkName: "applens-control" */ "@wishlink/applens-web"
          ),
          import(
            /* webpackChunkName: "applens-requests" */ "@wishlink/applens-web/requests"
          ),
          import(
            /* webpackChunkName: "applens-core" */ "@wishlink/applens-core"
          ),
        ]);
      } catch {
        return false;
      }
      const [module, requests, core] = loaded;
      coreRules = core;
      if (expected !== revision) return false;
      attachHeaders = requests.attachRequestHeaders;
      normalizeIdentity = core.normalizeIdentity;
      const normalized = normalizeIdentity(input.identity);
      if (
        !normalized ||
        normalized.runtime !== "web" ||
        !(mode === "local"
          ? normalized.environment === "local"
          : ["staging", "production"].includes(normalized.environment))
      )
        return false;
      identity = JSON.stringify(normalized);
      sdk = module.createWebController({
        mode,
        ...(mode === "deployment"
          ? { destination, allowDeploymentPropagation: deploymentPropagation }
          : {}),
        identity: normalized,
        origin: deps.origin,
        clock: deps.clock,
        schedule: deps.schedule,
        loadBinding: deps.loadBinding,
        instrumentationName: "wishlink-creator",
        endpoints: RELEASE_2_ENDPOINTS.map(({ name, owner }) => ({
          name,
          owner,
        })),
        journeys: [
          {
            name: "diy_single_product",
            taxonomy: {
              product: "creator",
              feature: "diy",
              flow: "single_product",
            },
          },
        ],
        errors: HOME_ERRORS,
        bootstrap: {
          timeoutMs: Math.max(
            1,
            Math.min(
              60000,
              (deps.bootstrapDeadline ?? Infinity) - deps.clock.now()
            )
          ),
          maxStarts: 64,
          taxonomy: { product: "creator", feature: "navigation", flow: "home" },
        },
        healthScreens: ["home"],
        screens: [
          {
            routeId: "login",
            taxonomy: {
              product: "creator",
              feature: "navigation",
              flow: "login",
            },
            readTerminalAttributes() {
              return terminalReader?.();
            },
          },
          {
            routeId: "home",
            taxonomy: {
              product: "creator",
              feature: "navigation",
              flow: "home",
            },
            terminalAttributes: HOME_TERMINAL_SCHEMA,
            readTerminalAttributes() {
              return terminalReader?.();
            },
          },
        ],
        lifecycle: deps.lifecycle && {
          listen(hidden, visible) {
            return deps.lifecycle!.listen(
              () => {
                suspended = true;
                hiddenAt = deps.clock.now();
                if (navigation) navigation.closed = true;
                hidden();
              },
              () => {
                suspended = false;
                visible();
                if (
                  hiddenAt !== undefined &&
                  deps.clock.now() - hiddenAt > 300000
                )
                  safely(
                    () => diy.capture()?.finish("backgrounded"),
                    undefined
                  );
                hiddenAt = undefined;
              }
            );
          },
        },
      });
    }
    const permitted =
      requestedMode === mode &&
      identity === JSON.stringify(normalizeIdentity!(input.identity)) &&
      (mode !== "local" ||
        (input as LocalConfig).exporterPath === "/client/v1/traces");
    sdk.setContext(safely(() => deps.signals?.context(), {}) ?? {});
    const enabled = await sdk.configure(
      permitted ? input : { ...input, enabled: false }
    );
    if (!enabled || expected !== revision) return false;
    config = Object.freeze({
      ...input,
      apiOrigins: Object.freeze([...input.apiOrigins]),
    }) as Readonly<LocalConfig>;
    removeSignals = deps.signals?.listen(() =>
      safely(() => sdk?.setContext(deps.signals?.context() ?? {}), undefined)
    );
    return true;
  }
  // Optional until beta.2 is published and consumed in its own exact-version PR.
  function renewLease(input: DeploymentConfig): boolean {
    if (
      !active() ||
      !sdk ||
      !config ||
      input.generation <= generation ||
      identity !== JSON.stringify(normalizeIdentity!(input.identity))
    )
      return false;
    const renewable = sdk as WebController & {
      renewLease?: (activation: DeploymentConfig) => boolean;
    };
    if (!renewable.renewLease?.(input)) return false;
    generation = input.generation;
    config = Object.freeze({
      ...config,
      generation,
      expiresAt: input.expiresAt,
    });
    return true;
  }
  function enter(
    key: string,
    routeInput: boolean | "home" | "login" | "other"
  ): HomeHandle {
    const route =
      typeof routeInput === "boolean"
        ? routeInput
          ? "home"
          : "other"
        : routeInput;
    const isHome = route === "home";
    const epoch = deps.clock.epochNow();
    if (navigation?.handle.key === key) {
      navigation.references++;
      return navigation.handle;
    }
    safely(() => navigation?.handle.finish({ outcome: "cancelled" }), false);
    const expected = revision;
    const record: Navigation = {
      handle: undefined as unknown as HomeHandle,
      references: 1,
      state: {},
      closed: route === "other" || !active(),
      route,
      intent: coreRules
        ? createIntentDependency(
            epoch,
            safely(readDependencies, {}).intent ?? { enabled: false },
            coreRules.dependencyState
          )
        : () => ({ "app.dep.intent": "absent" }),
      dependencies:
        coreRules?.createDependencies(
          deps.clock.epochNow(),
          safely(readDependencies, {})
        ) ??
        (() => ({
          "app.dep.creator_config": "absent",
          "app.dep.creator_data": "absent",
        })),
    };
    sdk?.setContext(deps.signals?.context() ?? {});
    const attempt = sdk?.startScreen(route, key);
    record.attempt = attempt;
    navigation = record;
    terminalReader = () => {
      record.closed = true;
      if (!isHome) return undefined;
      const snapshot = safely(readDependencies, {});
      record.attempt?.setDependencies(snapshot);
      return {
        ...record.dependencies(snapshot),
        ...record.intent(snapshot.intent ?? { enabled: false }),
      };
    };
    if (isHome) {
      const startType = deps.signals?.start(firstHome, deps.clock.now());
      if (!record.closed && startType) attempt?.setStartType(startType);
      if (!record.closed) {
        const anchor = deps.signals?.claimLaunch(
          firstHome,
          startType === "cold"
        );
        if (anchor !== undefined)
          record.launch = deps.clock.now() - (epoch - anchor);
      }
      firstHome = false;
    }
    if (!record.closed) loaders.forEach(() => attempt?.loader("start"));
    const root = !record.closed
      ? {
          get closed() {
            return record.closed || revision !== expected || !active();
          },
        }
      : undefined;
    const handle: HomeHandle = {
      key,
      root,
      finish: (completion) =>
        safely(() => {
          attempt?.setDependencies(safely(readDependencies, {}));
          if (
            record.launch !== undefined &&
            ["ready", "degraded"].includes(completion.outcome)
          )
            attempt?.setLaunch({
              launchToContentMs: record.launchContent,
              launchToReadyMs: deps.clock.now() - record.launch,
            });
          return attempt?.finish(completion) ?? false;
        }, false),
      report(state) {
        if (navigation !== record || !root || root.closed) return;
        record.state = { ...record.state, ...state };
        if (record.pending) return;
        record.pending = true;
        deps.defer(() => {
          record.pending = false;
          if (navigation !== record || root.closed) return;
          if (
            record.launch !== undefined &&
            !record.content &&
            record.state.branchCommitted
          ) {
            record.content = true;
            record.launchContent = deps.clock.now() - record.launch;
            attempt?.setLaunch({ launchToContentMs: record.launchContent });
          }
          const completion =
            record.route === "login"
              ? record.state.interactive && !loaders.size
                ? { outcome: "ready" as const }
                : undefined
              : homeCompletion(record.state);
          if (
            loaders.size &&
            ["ready", "degraded"].includes(completion?.outcome ?? "")
          )
            return;
          if (completion) handle.finish(completion);
        });
      },
    };
    record.handle = handle;
    return handle;
  }
  return {
    configure: (input: LocalConfig) => configure(input, "local"),
    configureDeployment: (input: DeploymentConfig) =>
      configure(input, "deployment"),
    renewLease,
    setDependencyReader(
      reader: () => DependencyTimings & { intent?: IntentSnapshot }
    ) {
      readDependencies = reader;
    },
    attachRequestHeaders: (
      request: Request,
      observation?: Parameters<NonNullable<typeof attachHeaders>>[1]
    ) => attachHeaders?.(request, observation) ?? request,
    queryTiming: (
      query: Parameters<NonNullable<typeof coreRules>["queryTiming"]>[0]
    ) => coreRules?.queryTiming(query) ?? {},
    captureRequest: (name: string) =>
      !navigation?.handle.root &&
      !diy.capture() &&
      deps.clock.now() >= (deps.bootstrapDeadline ?? Infinity)
        ? undefined
        : sdk?.captureRequest(name),
    startRequest: (...args: Parameters<WebController["startRequest"]>) =>
      sdk?.startRequest(...args),
    exportHealth: () =>
      sdk?.exportHealth() ?? {
        disabled: { batches: 0, spans: 0 },
        busy: { batches: 0, spans: 0 },
        serialization: { batches: 0, spans: 0 },
        failed: { batches: 0, spans: 0 },
      },
    disable,
    enter,
    diy,
    acquireLoader() {
      const token = {};
      loaders.add(token);
      const screen = navigation;
      const journey = diy.capture();
      screen?.attempt?.loader("start");
      journey?.loader("start");
      let released = false;
      return () =>
        safely(() => {
          if (released) return;
          released = true;
          loaders.delete(token);
          // A loader already visible at route entry belongs to that new screen too.
          navigation?.attempt?.loader("end");
          diy.capture()?.loader("end");
          navigation?.handle.report({});
        }, undefined);
    },
    route(path: string) {
      diy.route(path);
    },
    leave(handle: HomeHandle) {
      if (navigation?.handle !== handle) return;
      const record = navigation;
      record.references--;
      deps.defer(() => {
        if (record.references <= 0) handle.finish({ outcome: "cancelled" });
      });
    },
    captureHome: () =>
      navigation?.route === "home" &&
      navigation?.handle.root &&
      !navigation.handle.root.closed
        ? navigation.handle
        : undefined,
    activeConfig: active,
  };
}
export type Controller = ReturnType<typeof createController>;
